<template>
  <div class="h-100">
    <order v-if="!!order" :initial-order="order" @back="back($event)"/>
    <mdb-container v-else>
      <div class="d-flex justify-content-center align-items-center">
        <mdb-switch
          size="lg"
          class="mb-5"
          color="cyan"
          off-label="Client"
          on-label="Agent"
          :checked="isAgentContext"
          @getValue="setType($event)"
        />
      </div>
      <div class="card card-cascade narrower">
        <div
          class="
            orders-table-panel
            view
            view-cascade
            gradient-card-header
            narrower
            py-2
            mx-4
            mb-3
            d-flex
            flex-column
            align-items-around
          "
          :class="gradientClass"
        >
          <div class="d-flex justify-content-between align-items-center w-100 px-2 py-4">
            <a @click="getOrders" class="black-link">
              <mdb-icon icon="redo-alt" class="fa-3x"/>
            </a>
            <div>
              <mdb-dropdown multi-level title="Add columns">
                <mdb-dropdown-toggle
                  rounded
                  size="sm"
                  slot="toggle"
                  :class="gradientClass">+</mdb-dropdown-toggle>
                <mdb-dropdown-menu>
                  <ul class="list-unstyled">
                    <li
                      v-for="(column, i) in Array.from(allColumns).filter(col => col.index !== null)"
                      :key="`columns-checkbox-${i}`"
                    >
                      <mdb-input
                        type="checkbox"
                        :id="`columns-checkbox-${i}`"
                        :name="`columns-checkbox-${i}`"
                        :label="column.label"
                        v-model="selectedColumns[type][column.index]"
                        @change="onColumnChange()"
                      />
                    </li>
                  </ul>
                </mdb-dropdown-menu>
              </mdb-dropdown>
            </div>
          </div>
        </div>
        <orders-table
          :data="{columns: columns, rows: orders[type]}"
          max-width="95%"
          min-width="95%"
          :responsive="false"
          :type="type"
          @hide-column="onColumnChange($event, false)"
        />
      </div>
    </mdb-container>
  </div>
</template>

<script>
import { mapActions, mapState, mapGetters } from 'vuex'
import {capitalize, errorCatch, formatDT} from '../utils'
import {OrderStatuses, OrderTypes} from '../enums'
import Order from './Order'
import OrdersTable from './OrdersTable'
import {
  mdbSwitch,
  mdbInput,
  mdbContainer,
  mdbDropdown,
  mdbDropdownToggle,
  mdbDropdownMenu,
  mdbIcon
} from 'mdbvue'

export default {
  name: 'Orders',
  data () {
    return {
      order: null,
      type: OrderTypes.CLIENT,
      orders: {
        agent: [],
        client: []
      },
      selectedColumns: {
        agent: [],
        client: []
      }
    }
  },
  props: {
    uuid: {
      type: String,
      default: null
    }
  },
  computed: {
    ...mapState('user', {
      stateSelectedColumns: state => state.ordersTableColumns
    }),
    ...mapState('calc', {
      prevOrder: state => state.order
    }),
    ...mapGetters('user', ['isAuthenticated', 'isUser']),
    isAgentContext: function () {
      return this.type === OrderTypes.AGENT
    },
    gradientClass: function () {
      return {[this.type === OrderTypes.CLIENT ? 'aqua-gradient' : 'deep-blue-gradient']: true}
    },
    allColumns: function () {
      return [
        {
          index: null,
          label: '',
          componentName: 'OrdersTableCellEdit'
        },
        {
          index: 1,
          label: 'Created',
          componentName: 'OrdersTableCellCreated',
          sortCallback: row => formatDT(row.info.created),
          sort: 'desc'
        },
        {
          index: 2,
          label: 'Name',
          componentName: 'OrdersTableCellName',
          sortCallback: row => row.info.name || `#${row.info.number}`,
          sort: 'asc'
        },
        {
          index: 3,
          label: capitalize(this.type),
          // componentName: `OrdersTableCell${this.type}`,
          componentName: `OrdersTableCellPartner`,
          sortCallback: row => row[this.type].name,
          sort: 'asc'
        },
        {
          index: 4,
          label: 'Status',
          componentName: 'OrdersTableCellStatus',
          sortCallback: row => Object.keys(OrderStatuses).indexOf(row.info.status.code),
          sort: 'asc'
        },
        {
          index: 5,
          label: 'Manager',
          componentName: 'OrdersTableCellManager',
          sortCallback: row => row.author.username,
          sort: 'asc'
        }
      ]
    },
    columns: function () {
      return this.allColumns.filter(column => {
        return column.index === null || this.selectedColumns[this.type][column.index]
      })
    }
  },
  methods: {
    ...mapActions(['apiOrdersGet', 'apiOrderGetInfo']),
    ...mapActions('calc', ['setLinked']),
    ...mapActions('user', ['setOrdersTableColumns']),
    getOrders () {
      this.apiOrdersGet()
        .then(({client, agent}) => {
          this.orders.client = client
          this.orders.agent = agent
        })
        .catch(errorCatch('Get Orders Failed', this))
    },
    setType (type) {
      if (typeof type === 'boolean') {
        this.type = type ? OrderTypes.AGENT : OrderTypes.CLIENT
      } else {
        this.type = type
      }
    },
    onColumnChange (index = null, value = null) {
      if (index !== null && value !== null) {
        this.$set(this.selectedColumns[this.type], index, value)
      }
      const newSelectedColumns = []
      for (let [key, value] of Object.entries(this.selectedColumns[this.type])) {
        if (value) {
          newSelectedColumns.push(parseInt(key))
        }
      }
      if (JSON.stringify(this.stateSelectedColumns[this.type]) !== JSON.stringify(newSelectedColumns)) {
        this.setOrdersTableColumns({columns: newSelectedColumns, type: this.type})
          .then(() => {
            if (this.isAuthenticated) {
              this.$notify.info({
                title: `${capitalize(this.type)} Orders Table Columns are saved.`,
                message: '',
                position: 'top right',
                timeOut: 5000,
                closeButton: true
              })
            }
          })
          .catch(errorCatch(`${capitalize(this.type)} Orders Table Columns save`, this))
      }
    },
    back (context) {
      this.order = null
      this.setType(context)
      this.getOrders()
    }
  },
  mounted () {
    this.$bus.$on('orders-edit-order', order => {
      this.order = order
    })
    this.$bus.$on('order-name-change', ({uuid, name}) => {
      const agentIndex = this.orders.agent.findIndex(ord => ord.info.uuid === uuid)
      if (agentIndex !== -1) {
        if (name) {
          this.$set(this.orders.agent[agentIndex].info, 'name', name)
        } else {
          this.$delete(this.orders.agent[agentIndex].info, 'name')
        }
        return
      }
      const clientIndex = this.orders.client.findIndex(ord => ord.info.uuid === uuid)
      if (clientIndex !== -1) {
        if (name) {
          this.$set(this.orders.client[clientIndex].info, 'name', name)
        } else {
          this.$delete(this.orders.client[clientIndex].info, 'name')
        }
      }
    })
    this.getOrders()
    const calculateSelectedColumns = () => {
      for (const prop in this.selectedColumns) {
        const selectedColumns = {}
        this.allColumns.filter(col => col.index !== null)
          .forEach(col => {
            selectedColumns[col.index] = (this.stateSelectedColumns[prop] || []).includes(col.index)
          })
        this.selectedColumns[prop] = selectedColumns
      }
    }
    this.$bus.$on('profile-fetch-completed', calculateSelectedColumns)
    calculateSelectedColumns()
  },
  beforeDestroy () {
    this.$bus.$off('orders-edit-order')
  },
  beforeRouteEnter (to, from, next) {
    // TODO: WHAT THE CRUNCH !!!
    next(vm => {
      vm.order = vm.prevOrder
      vm.setLinked(null)
    })
  },
  watch: {
    uuid (newVal) {
      if (newVal) {
        this.apiOrderGetInfo(newVal)
          .then(order => { this.order = order })
          .catch(errorCatch('Order Get Info Failed', this))
      }
    },
    order (newVal) {
      if (newVal) {
        this.$router.replace(`/orders/${newVal.info.uuid}`)
      }
    }
  },
  components: {
    mdbSwitch,
    mdbInput,
    mdbContainer,
    mdbDropdown,
    mdbDropdownToggle,
    mdbDropdownMenu,
    OrdersTable,
    Order,
    mdbIcon
  }
}
</script>

<style lang="sass">
.orders-table-panel
  overflow: visible

  td
    word-break: break-word
</style>



// WEBPACK FOOTER //
// src/components/Orders.vue